<template>
  <div>
    <EntityShowHeader :entity="entity"/>
    <DataPage
      ref="entityProductsList"
      :columns="columns"
      :fetch-callback="fetchEntityProducts"
      :model="EntityProductModel"
      :header-underline="false"
      has-row-actions
    >
      <template #level>
        <Badge variant="soft-primary">
          {{ __('badges.parent') }}
        </Badge>
      </template>

      <template #afterItem="{ item }">
        <tr v-for="(child, key) in item.children" :key="`${key}_${Math.random()}`" class="row-tight">
          <td>
            <Icon icon="corner-down-right" size="12"/>
            {{ child.name }}
          </td>
          <td>
            <Badge variant="soft-secondary">
              {{ __('badges.child') }}
            </Badge>
          </td>
          <td>
            {{ child.tag }}
          </td>
          <td>
            {{ child.commission }}
          </td>
          <td>
            {{ child.grossAmount }}
          </td>
          <td>
            {{ child.feeAmount }}
          </td>
          <td v-if="isEntityProductHistoryEnabled">
            {{ child.activeFrom }}
          </td>
          <td>
            <ActionPopover :id="child.id" v-can-any="[ 'products.update', 'entity-products.unlink' ]">
              <b-button
                v-can:products.update
                class="dropdown-item"
                size="sm"
                @click="editLinkedProduct(child)"
              >
                <Icon icon="edit" class="mr-2" size="14"/>
                {{ __('buttons.edit') }}
              </b-button>
            </ActionPopover>
          </td>
        </tr>
      </template>

      <template #tableHeaderActions>
        <b-button
          v-can:products.create
          variant="primary"
          size="sm"
          data-testid="link-product-btn"
          @click="openLinkProductModal"
        >
          <Icon icon="plus-circle" size="14" class="mr-2"/>
          {{ __('entities.link-product') }}
        </b-button>
      </template>

      <template #actionButtons="{ item }">
        <ActionPopover :id="item.id" v-can-any="[ 'products.update', 'entity-products.unlink' ]">
          <b-button
            v-can:products.update
            class="dropdown-item"
            size="sm"
            @click="isEntityProductHistoryEnabled ? openProductHistory(item) : editLinkedProduct(item)"
          >
            <Icon icon="edit" class="mr-2" size="14"/>
            {{ __('buttons.edit') }}
          </b-button>
          <b-button
            v-can:products.update
            class="dropdown-item"
            size="sm"
            @click="unlink(item)"
          >
            <Icon icon="edit" class="mr-2" size="14"/>
            {{ __('entities.button-unlink') }}
          </b-button>
        </ActionPopover>
      </template>
    </DataPage>
  </div>
</template>

<script>
import LazyLoadComponent from '@/General/LazyLoadComponent.js'
import EntityShowHeader from '@/Modules/Entity/Components/EntityShowHeader.vue'
import DataPage from '@/General/Tables/DataPage.vue'
import EntityProductModel from '@/Modules/Entity/EntityProduct/EntityProductModel.js'
import EntityProductService from '@/Modules/Entity/EntityProduct/EntityProductService.js'
import LinkProductModal from '@/Modules/Entity/Components/LinkProductModal.vue'
import EntityProductHistoryModal from '@/Modules/Entity/Components/EntityProductHistoryModal.vue'
import EntityService from '@/Modules/Entity/EntityService.js'
import { ENTITY_PRODUCT_HISTORY_GETTER } from '@/Modules/Base/SettingModule.js'

export default {
  name: 'List',

  components: { DataPage, EntityShowHeader },

  extends: LazyLoadComponent((to, callback) => {
    EntityService
        .show(to.params.entityId)
        .then(entity => {
          callback(function () {
            this.entity = entity
          })
        })
  }),

  data () {
    return {
      EntityProductModel: EntityProductModel,
      entity: null,
    }
  },

  computed: {
    isEntityProductHistoryEnabled() {
      return this.$store.getters[`SettingModule/${ENTITY_PRODUCT_HISTORY_GETTER}`]
    },

    columns() {
      const cols = [
        {
          column: 'name',
          title: this.__(`common.name`),
          sortable: true,
        },
        {
          column: 'level',
          title: this.__(`common.level`),
        },
        {
          column: 'tag',
          title: this.__(`common.tag`),
        },
        {
          column: 'commission',
          title: this.__('common.agent-commission') + ', %',
          sortable: true,
        },
        {
          column: 'grossAmount',
          title: this.__(`common.gross-commission`),
          sortable: true,
        },
        {
          column: 'feeAmount',
          title: this.__(`entities.fee`),
          sortable: true,
        },
      ]

      if (this.isEntityProductHistoryEnabled) {
        cols.push({
          column: 'activeFrom',
          title: this.__(`entities.active-from`),
        })
      }

      return cols
    },
  },

  methods: {
    fetchEntityProducts (parameters) {
      return EntityProductService.list(this.entity.id, parameters)
    },

    openLinkProductModal () {
      this.showModal({
        component: LinkProductModal,
        label: this.__(`entities.link-product`),
        icon: 'link',
        class: 'modal-md',
        onSuccess: () => {
          this.$refs.entityProductsList.refreshTable()
          this.fetchEntity()
        }
      })
    },

    unlink (item) {
      this.$showConfirmationModal(
          (this.__(`entities.product-unlink-confirmation`)),
          () => {
            const request = this.isEntityProductHistoryEnabled
              ? EntityProductService.unlink(this.entity.id, item.productId)
              : EntityProductService.delete(item.id)

            request.then(() => {
              this.$successToast(this.__(`entities.product-unlink-success`))
              this.$refs.entityProductsList.refreshTable()
              this.fetchEntity()
            })
          }
      )
    },

    editLinkedProduct (product) {
      this.showModal({
        component: LinkProductModal,
        label: this.__(`entities.linked-product`),
        icon: 'link',
        class: 'modal-md',
        meta: {
          product
        },
        onSuccess: () => {
          this.$refs.entityProductsList.refreshTable()
        }
      })
    },

    openProductHistory(item) {
      this.showModal({
        component: EntityProductHistoryModal,
        label: 'Product: ' + item.name,
        class: 'modal-lg',
        meta: {
          entityId: this.entity.id,
          productId: item.productId,
          productName: item.name,
        },
        onClose: () => {
          this.$refs.entityProductsList.refreshTable()
        },
      })
    },

    fetchEntity() {
      EntityService
        .show(this.$route.params.entityId)
        .then(entity => {
            this.entity = entity
        })
    }
  }
}
</script>

<style scoped>

</style>
